import { ApolloClient, gql } from "@apollo/client";
import { useEffect, useState } from "react";
import _ from "lodash";
import {
  BuildingIcon,
  GitBranchIcon,
  MailboxIcon,
  MapPinIcon,
  SmartphoneIcon,
  TextAlignStartIcon,
  UsersIcon,
} from "lucide-react";

import { ContactEnum, ContactFragment } from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Popper } from "@/components/atoms/Popper";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { useEntity } from "@/hooks/useEntity";
import { EnumColors, Values } from "@/constants/values";
import { iconsByTypename } from "@/objects/@icons";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

type ContactOption = ContactFragment & {
  isMovable?: boolean;
  entity?: { id: string; identifier: string; name?: string | null } | null;
};

const COPY_CONTACT_TO_ENTITY = gql`
  mutation CopyContactToEntity($input: CopyNodeToEntityInput!) {
    copyNodeToEntity(input: $input) {
      data {
        ... on ContactObject {
          ...Contact
          isMovable
        }
      }
    }
  }
`;

const MOVE_CONTACT_TO_ENTITY = gql`
  mutation MoveContactToEntity($input: MoveNodeToEntityInput!) {
    moveNodeToEntity(input: $input) {
      data {
        ... on ContactObject {
          ...Contact
          isMovable
        }
      }
    }
  }
`;

/** The heading over a picker's contacts from one other entity. */
function ContactEntityGroupHeading({ identifier }: { identifier: string }) {
  const { entity } = useEntity(identifier);
  return (
    <div
      data-testid={`contact-group-${identifier}`}
      className="flex items-center gap-2"
    >
      <BoxForEntity
        identifier={identifier}
        representation="character"
        size="sm"
      />
      <Typography level="body-xs" className="text-muted-foreground/80">
        {entity?.name ?? "Another entity"}
      </Typography>
    </div>
  );
}

/** One way to bring a contact into the entity: a title, what it does, and why it cannot when it cannot. */
function ContactBringOption({
  title,
  description,
  disabledReason,
  isPending,
  onClick,
}: {
  title: string;
  description: string;
  disabledReason?: string;
  isPending: boolean;
  onClick: (event: React.MouseEvent) => void;
}) {
  const isDisabled = Boolean(disabledReason) || isPending;
  const option = (
    <div
      role="button"
      aria-disabled={isDisabled}
      data-testid={`contact-bring-${title.toLowerCase()}`}
      onClick={isDisabled ? undefined : onClick}
      className={cn(
        "pl-2 pr-2 pt-1.5 pb-1.5 rounded-sm",
        isDisabled ? "cursor-not-allowed" : "cursor-pointer",
        disabledReason ? "opacity-[0.5]" : "opacity-[1]",
        isDisabled ? "" : "hover:bg-muted",
      )}
    >
      <Typography level="body-sm" className="font-medium">
        {title}
      </Typography>
      <Typography level="body-xs" className="text-muted-foreground/80">
        {description}
      </Typography>
    </div>
  );
  return disabledReason ? (
    <Tooltip>
      <TooltipTrigger asChild>{option}</TooltipTrigger>
      <TooltipContent side="right">{disabledReason}</TooltipContent>
    </Tooltip>
  ) : (
    option
  );
}

/**
 * A contact in a picker: its entity, its name and email, and — when it belongs
 * to another entity than the one it is wanted in — a link to bring it over,
 * which offers copying it, or moving it while no document or transaction uses it.
 */
function ContactOptionRow({
  contact,
  entityIdentifier,
  helpers,
  popperOpeners,
}: {
  contact: ContactOption;
  entityIdentifier: string | null;
  helpers?: {
    select: (option: ContactOption) => void;
    apolloClient: ApolloClient;
  };
  /** Where the row leaves a way to open its popper, for a click on the row itself. */
  popperOpeners: Map<string, () => void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    popperOpeners.set(contact.identifier, () => setIsOpen(true));
    return () => {
      popperOpeners.delete(contact.identifier);
    };
  }, [popperOpeners, contact.identifier]);
  const [pending, setPending] = useState<"copy" | "move" | null>(null);
  const { entity: target } = useEntity(entityIdentifier ?? undefined);
  const { entity: source } = useEntity(contact.entity?.identifier);
  const isElsewhere =
    !!entityIdentifier &&
    !!contact.entity?.identifier &&
    contact.entity.identifier !== entityIdentifier;
  const targetName = target?.name ?? "this entity";
  const sourceName = source?.name ?? contact.entity?.name ?? "its entity";

  const bring =
    (action: "copy" | "move") => async (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!helpers || !entityIdentifier) return;
      setPending(action);
      try {
        const key = action === "copy" ? "copyNodeToEntity" : "moveNodeToEntity";
        const { data } = await helpers.apolloClient.mutate<Record<string, any>>(
          {
            mutation:
              action === "copy"
                ? COPY_CONTACT_TO_ENTITY
                : MOVE_CONTACT_TO_ENTITY,
            variables: { input: { id: contact.id, entityIdentifier } },
            refetchQueries: ["ContactList"],
          },
        );
        // ``null`` when the server refused: nothing goes onto the field.
        const brought = data?.[key]?.data?.[0] as ContactOption | undefined;
        if (brought) {
          setIsOpen(false);
          helpers.select(brought);
        }
      } finally {
        setPending(null);
      }
    };

  return (
    <div
      data-testid={`contact-option-${contact.identifier}`}
      className="flex items-center gap-2 min-w-0 w-full"
    >
      {/* A filter's rows are given their box where every filter's are. */}
      {entityIdentifier && (
        <BoxForEntity
          identifier={contact.entity?.identifier}
          representation="character"
        />
      )}
      <div className="min-w-0 flex-1">
        <Typography
          level="body-sm"
          className={cn(
            "truncate",
            contact.name ? "" : "text-muted-foreground",
          )}
        >
          {contact.name || "No name set..."}
        </Typography>
        {contact.emailAddress && (
          <Typography
            level="body-xs"
            className="truncate text-muted-foreground/80"
          >
            {contact.emailAddress}
          </Typography>
        )}
      </div>
      {isElsewhere && (
        // A click on the link or in its popper is not a click on the row.
        <div
          className="shrink-0"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Popper
            isOpenState={[isOpen, setIsOpen]}
            isHoverable
            placement="right-start"
            anchor={
              <Typography
                level="body-xs"
                data-testid={`contact-bring-${contact.identifier}`}
                className="text-muted-foreground cursor-pointer hover:[text-decoration:underline]"
              >
                Apply
              </Typography>
            }
          >
            <div className="p-1 w-65">
              <ContactBringOption
                title="Copy"
                description={`Adds a copy to ${targetName}. The original stays in ${sourceName}.`}
                isPending={pending !== null}
                onClick={bring("copy")}
              />
              <ContactBringOption
                title="Move"
                description={`Moves this contact from ${sourceName} to ${targetName}.`}
                disabledReason={
                  contact.isMovable
                    ? undefined
                    : `Documents or transactions in ${sourceName} use this contact, so it can only be copied.`
                }
                isPending={pending !== null}
                onClick={bring("move")}
              />
            </div>
          </Popper>
        </div>
      )}
    </div>
  );
}

function contactTypeColor(type: ContactEnum | null | undefined) {
  if (type === ContactEnum.Supplier) return EnumColors.RED;
  if (type === ContactEnum.Employee) return EnumColors.BLUE;
  if (type === ContactEnum.Customer) return EnumColors.ORANGE;
  return EnumColors.GREY_1;
}

function ContactCard({
  data,
  actions: _actions,
  isLoading = false,
}: ObjectCardProps<ContactFragment>) {
  const email = data?.emailAddress;
  const organization = data?.organization;
  const phone = data?.phoneNumber;

  return (
    <div className="flex flex-col gap-1.5 h-full min-h-0">
      {email && (
        <Typography level="body-sm" className="truncate text-muted-foreground">
          <Skeleton loading={isLoading} variant="inline">
            {email}
          </Skeleton>
        </Typography>
      )}

      {organization && (
        <Typography
          level="body-xs"
          className="truncate text-muted-foreground/80"
        >
          <Skeleton loading={isLoading} variant="inline">
            {organization.name}
          </Skeleton>
        </Typography>
      )}

      {phone && (
        <Typography
          level="body-xs"
          className="truncate text-muted-foreground/80"
        >
          <Skeleton loading={isLoading} variant="inline">
            {phone}
          </Skeleton>
        </Typography>
      )}

      {data?.type && (
        <div className="mt-auto">
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label={_.startCase(data.type.toString().toLowerCase())}
              enumColor={contactTypeColor(data.type as ContactEnum)}
              size="sm"
            />
          </Skeleton>
        </div>
      )}
    </div>
  );
}

const object: ObjectType<ContactFragment> = {
  humanName: "contact",
  typename: "ContactObject",
  report: {
    source: "contacts",
    description: "The people and businesses on either side of the money",
    asks: "Who do we spend the most with?",
  },
  ReactIcon: UsersIcon,
  isEntity: false,
  titleFieldKey: "name",
  Card: ContactCard,
  placeholder: {
    __typename: "ContactObject",
    id: btoa("ContactObject:placeholder"),
    identifier: "placeholder",
    name: "Dana Whitfield",
    emailAddress: "dana@northwind.example",
    phoneNumber: "(604) 555-0142",
    type: ContactEnum.Supplier,
    organization: {
      id: btoa("OrganizationObject:placeholder"),
      identifier: "placeholder",
      name: "Northwind Supply",
      logoUrl: null,
    },
    entity: {
      id: btoa("EntityObject:placeholder"),
      identifier: "placeholder",
      name: "Elm Street Coffee",
    },
  } as ContactFragment,
  getTitle: (data) => {
    return data?.name || data?.emailAddress;
  },
  getEntityId: (data) => {
    return data?.entity?.id;
  },
  getOrganization: (data) => data?.organization,
  // For a field, every entity's contacts, the entity's own first: one found
  // elsewhere is copied in, or moved while nothing uses it, instead of
  // entered again. For a filter, the contacts of the entities it looks across.
  getReferenceValueOptions: (entityIdentifiers, purpose = "value") => {
    // The entity a picked contact goes into. A filter's goes into none, so
    // nothing is elsewhere: no headings, no Copy / Move, no own entity first.
    const entityIdentifier =
      purpose === "value" ? (entityIdentifiers?.[0] ?? null) : null;
    // Each row from another entity leaves here how to open its Copy / Move
    // popper, which choosing the row itself does.
    const popperOpeners = new Map<string, () => void>();
    return {
      query: gql`
        query ContactList($entityIdentifiers: [String!]) {
          contacts(entityIdentifiers: $entityIdentifiers) {
            data {
              id
              identifier
              name
              emailAddress
              organization {
                id
                identifier
                name
                logoUrl
              }
              type
              isMovable
              entity {
                id
                identifier
                name
              }
            }
          }
        }
      `,
      // A field's lists every entity's contacts, to bring one over from.
      queryOptions: {
        variables: purpose === "filter" ? { entityIdentifiers } : {},
      },
      // The entity's own first, then each other entity's together, so each
      // run sits under its heading.
      prepareOptions: (data: unknown) => {
        const contacts = ((data as { contacts?: { data?: ContactOption[] } })
          ?.contacts?.data ?? []) as ContactOption[];
        return entityIdentifier
          ? _.sortBy(
              contacts,
              (contact) =>
                contact.entity?.identifier === entityIdentifier ? 0 : 1,
              (contact) => contact.entity?.name ?? "",
              (contact) => contact.entity?.identifier ?? "",
            )
          : contacts;
      },
      getOptionGroup: entityIdentifier
        ? (contact: ContactOption) =>
            !contact.entity?.identifier ||
            contact.entity.identifier === entityIdentifier
              ? null
              : {
                  key: contact.entity.identifier,
                  label: (
                    <ContactEntityGroupHeading
                      identifier={contact.entity.identifier}
                    />
                  ),
                }
        : undefined,
      onRenderOption(
        contact: ContactOption,
        _index?: number,
        helpers?: {
          select: (option: ContactOption) => void;
          apolloClient: ApolloClient;
        },
      ) {
        return (
          <ContactOptionRow
            contact={contact}
            entityIdentifier={entityIdentifier}
            helpers={helpers}
            popperOpeners={popperOpeners}
          />
        );
      },
      filterOptions(options: ContactOption[], filter) {
        const needle = filter.toLowerCase();
        return options.filter(
          (option) =>
            option.name?.toLowerCase().includes(needle) ||
            option.emailAddress?.toLowerCase().includes(needle),
        );
      },
      // A row from another entity opens its Copy / Move popper rather than
      // going onto the field: a record never points at another entity's
      // contact, and whether to copy or move it is the user's choice.
      resolveSelection: (contact: ContactOption) => {
        if (
          entityIdentifier &&
          contact.entity?.identifier &&
          contact.entity.identifier !== entityIdentifier
        ) {
          popperOpeners.get(contact.identifier)?.();
          return null;
        }
        return contact;
      },
      mutationOptions: {
        createTypename: "ContactObject",
        entityIdentifier,
      },
    };
  },
  fieldDefinitions: [
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.identifier,
      getValue: (data) => () => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoxForEntity
              identifier={data?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Contact",
      type: "reference",
      ReactIcon: iconsByTypename.ContactObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "ContactObject",
          name: data?.name || data?.emailAddress,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.entity?.id,
          logoUrl: data?.organization?.logoUrl,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("ContactObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name,
      getValue: (data) => {
        return data?.name;
      },
      getValueOptions: () => ({
        placeholder: "John Doe, etc.",
      }),
      // Who a person makes, they can name. Their email address is asked for
      // only when an invoice is sent to them.
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "emailAddress",
      label: "Email address",
      type: "string",
      ReactIcon: MailboxIcon,
      asString: (data) => data?.emailAddress,
      getValue: (data) => {
        return data?.emailAddress;
      },
      getValueOptions: () => ({
        placeholder: "john.doe@example.com, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // The company the contact is, or works for: one of the entity's
      // organizations, or a brand Brandfetch knows, picked as it is typed.
      objectKey: "organization",
      inputKey: "organization",
      label: "Organization",
      type: "reference",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.organization?.name,
      getValue: (data) =>
        data?.organization
          ? {
              __typename: "OrganizationObject",
              name: data.organization.name,
              id: data.organization.id,
              identifier: data.organization.identifier,
              entityId: data?.entity?.id,
            }
          : null,
      getValueOptions: (data) =>
        getReferenceValueOptionsByTypename("OrganizationObject")([
          data?.entity?.identifier,
        ]),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "reference",
        isFilterable: true,
      },
    },
    {
      objectKey: "billingAddress",
      label: "Billing address",
      type: "address",
      ReactIcon: MapPinIcon,
      asString: (data) =>
        [
          data?.billingAddress?.addressLine1,
          data?.billingAddress?.city,
          data?.billingAddress?.region,
          data?.billingAddress?.country,
          data?.billingAddress?.postalCode,
        ]
          .filter((x) => x != null)
          .join(", "),
      getValue: (data) => data?.billingAddress,
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "text",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "phoneNumber",
      label: "Phone number",
      type: "string",
      ReactIcon: SmartphoneIcon,
      asString: (data) => data?.phoneNumber,
      getValue: (data) => {
        return data?.phoneNumber;
      },
      getValueOptions: () => ({
        placeholder: "(123) 456-7890, etc.",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "type",
      label: "Type",
      type: "enum",
      ReactIcon: GitBranchIcon,
      asString: (data) => data?.type,
      getValue: (data) => data?.type,
      getValueOptions: () => ({
        enum: _.map(ContactEnum, (value, key) => ({
          key,
          value,
        })),
        getColors: (mode) => ({
          [ContactEnum.Supplier]: EnumColors.RED[mode],
          [ContactEnum.Employee]: EnumColors.BLUE[mode],
          [ContactEnum.Customer]: EnumColors.ORANGE[mode],
        }),
      }),
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
  ],
};

export { object as ContactObject };
